import { Layer, Stage } from "react-konva";
import styles from "./template-editor.module.css";
import { useTranslation } from "react-i18next";
import { ImageIcon, PlusIcon, TextIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef, useMemo } from "react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { KonvaEventObject } from "konva/lib/Node";
import Konva from "konva";
import { useHistory } from "@/hooks/use-history";
import { SortableLayerPreview } from "./components/layer-preview/sortable-layer-preview";
import { TextElementEditor } from "./components/text-element-editor/text-element-editor";
import { ImageElementEditor } from "./components/image-element-editor/image-element-editor";
import { FormField } from "@/components/ui/form-field";
import { ActionBar } from "./components/action-bar/action-bar";
import {
    DEFAULT_TEMPLATE_ACCENT_COLOR,
    ImageItem,
    LayerItem as SharedLayerItem,
    TemplateDto,
    TemplateSceneV1,
    TemplateStyle,
    TextItem,
} from "@exibra/shared";
import { getDefaultTextItem } from "./template-editor.helper";
import { getDefaultImageItem } from "./template-editor.helper";
import { v4 as uuidv4 } from "uuid";
import { TemplateFormConfig } from "./template-form.config";
import { useForm } from "react-hook-form";
import { templateFormConfig } from "./template-form.config";
import { FileDropzone } from "./components/file-dropzone/file-dropzone";
import { templateService } from "@/services/template.service";
import { toast } from "sonner";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AccentColorField } from "../../components/accent-color-field/accent-color-field";
import { typedZodResolver } from "@/lib/typed-zod-resolver";

const ASPECT_RATIO = 16 / 9;
const BASE_STAGE_WIDTH = 1920; // Base width for scaling calculations

const layerTypes = [
    {
        id: "text",
        icon: <TextIcon className="w-4 h-4" />,
    },
    {
        id: "image",
        icon: <ImageIcon className="w-4 h-4" />,
    },
];

interface EditorState {
    layers: LayerItem[];
    selectedId: string | null;
}

interface TemplateEditorCanvasProps {
    template: TemplateDto;
    onSave: (template: TemplateDto) => void;
}

// Re-export LayerItem for use in other components
export type LayerItem = SharedLayerItem;

export const TemplateEditorCanvas: React.FC<TemplateEditorCanvasProps> = ({ template, onSave }) => {
    const { t } = useTranslation();
    const v1Scene = template.scene as TemplateSceneV1;
    const [layers, setLayers] = useState<LayerItem[]>(v1Scene.elements ?? []);
    const [accentColor, setAccentColor] = useState(
        v1Scene.style?.accentColor ?? DEFAULT_TEMPLATE_ACCENT_COLOR,
    );
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const transformerRef = useRef<Konva.Transformer>(null);
    const [images, setImages] = useState<{ [key: string]: HTMLImageElement }>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const [stageSize, setStageSize] = useState({ width: 0, height: 0, scale: 1 });
    const selectedLayer = useMemo(
        () => layers.find((layer) => layer.id === selectedId),
        [layers, selectedId],
    );
    const actionBarRef = useRef<HTMLDivElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Function to handle drag end event
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            addToHistory();

            // Find indices of the dragged item and drop target
            const oldIndex = layers.findIndex((layer) => layer.id === active.id);
            const newIndex = layers.findIndex((layer) => layer.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                // Create a new layers array with the item in the new position
                const newLayers = [...layers];
                const movedItem = layers[oldIndex];
                if (movedItem) {
                    newLayers.splice(oldIndex, 1);
                    newLayers.splice(newIndex, 0, movedItem);

                    // Update state with the new order
                    setLayers(newLayers);
                }
            }
        }
    };

    const currentState: EditorState = {
        layers,
        selectedId,
    };

    const setState = (newState: EditorState) => {
        setLayers(newState.layers);
        setSelectedId(newState.selectedId);
    };

    const { addToHistory } = useHistory<EditorState>(currentState, setState);

    const {
        register,
        handleSubmit,
        formState: { errors, touchedFields, isValid },
    } = useForm<TemplateFormConfig>({
        resolver: typedZodResolver<TemplateFormConfig>(templateFormConfig),
        defaultValues: {
            name: template.name,
        },
        mode: "onChange",
    });

    useEffect(() => {
        const updateDimensions = () => {
            if (!containerRef.current) return;

            // Get dimensions of the editor container
            const editorWidth = containerRef.current.clientWidth;
            const editorHeight = containerRef.current.offsetHeight;

            // Get height of the action bar
            const actionBarHeight = actionBarRef.current?.offsetHeight ?? 0;

            // Subtract 2px for border and ActionBar height for available space
            const availableWidth = editorWidth - 2;
            const availableHeight = editorHeight - actionBarHeight - 2;

            // Ensure available dimensions are non-negative
            const safeAvailableWidth = Math.max(0, availableWidth);
            const safeAvailableHeight = Math.max(0, availableHeight);

            // Calculate the aspect ratio of the available space
            const availableAspectRatio = safeAvailableWidth / safeAvailableHeight;

            let width;
            let height;

            // Determine if width or height is the limiting factor to maintain 16:9 within available space
            if (availableAspectRatio > ASPECT_RATIO) {
                // Available space is wider than the target aspect ratio, so height is the limit
                height = safeAvailableHeight;
                width = height * ASPECT_RATIO;
            } else {
                // Available space is taller or equal to the target aspect ratio, so width is the limit
                width = safeAvailableWidth;
                height = width / ASPECT_RATIO;
            }

            // Ensure final dimensions are positive
            width = Math.max(1, width);
            height = Math.max(1, height);

            // Calculate scale based on base stage width
            const scale = width / BASE_STAGE_WIDTH;
            const finalScale = Math.max(0.001, scale); // Avoid zero scale

            setStageSize({ width, height, scale: finalScale });
        };

        // Initial update
        updateDimensions();

        // Attach window resize listener
        window.addEventListener("resize", updateDimensions);

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener("resize", updateDimensions);
        };
    }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

    useEffect(() => {
        layers.forEach((layer) => {
            if (layer.type === "image" && (layer as ImageItem).imageUrl && !images[layer.id]) {
                const img = new window.Image();
                const imageUrl = (layer as ImageItem).imageUrl;
                img.src = !imageUrl.startsWith("http")
                    ? `${import.meta.env.VITE_API_URL}${imageUrl}`
                    : imageUrl;
                img.onload = () => {
                    setImages((prev) => ({
                        ...prev,
                        [layer.id]: img,
                    }));
                };
            }
        });
    }, [layers, images]);

    useEffect(() => {
        if (selectedId && transformerRef.current) {
            const stage = transformerRef.current.getStage();
            if (!stage) return;

            // Clear existing nodes first
            transformerRef.current.nodes([]);

            const selectedLayer = layers.find((layer) => layer.id === selectedId);
            if (!selectedLayer) return;

            // Only apply transformer to text elements - image elements have their own transformer
            if (selectedLayer.type === "text") {
                const node = stage.findOne("#" + selectedId);
                if (node) {
                    transformerRef.current.setAttrs({
                        enabledAnchors: [
                            "top-left",
                            "top-right",
                            "bottom-left",
                            "bottom-right",
                            "middle-left",
                            "middle-right",
                        ],
                        keepRatio: false,
                    });

                    transformerRef.current.nodes([node]);
                    const layer = transformerRef.current.getLayer();
                    if (layer) {
                        layer.batchDraw();
                    }
                }
            }
        } else if (transformerRef.current) {
            // Clear nodes when nothing is selected
            transformerRef.current.nodes([]);
            const layer = transformerRef.current.getLayer();
            if (layer) {
                layer.batchDraw();
            }
        }
    }, [selectedId, images, layers]);

    const getNextLayerNumber = (type: string) => {
        const existingLayers = layers.filter((layer) => layer.type === type);
        return existingLayers.length + 1;
    };

    const onAddLayer = (layerType: string) => {
        addToHistory();
        const layerNumber = getNextLayerNumber(layerType);
        const newLayer =
            layerType === "text"
                ? getDefaultTextItem(layerNumber, 1920 / 2, 1080 / 2)
                : getDefaultImageItem(layerNumber, 1920 / 2, 1080 / 2);

        // Preload image if it's an image layer
        if (layerType === "image" && (newLayer as ImageItem).imageUrl) {
            const img = new window.Image();
            img.src = (newLayer as ImageItem).imageUrl;
            img.onload = () => {
                setImages((prev) => ({
                    ...prev,
                    [newLayer.id]: img,
                }));
            };
        }

        setLayers([...layers, newLayer]);
        // Automatically select the new layer
        setSelectedId(newLayer.id);
    };

    /**
     * Deletes a layer from the layers array
     */
    const onDelete = (layer: LayerItem) => {
        addToHistory();
        setLayers(layers.filter((l) => l.id !== layer.id));
    };

    /**
     * Duplicates a layer from the layers array
     */
    const onDuplicate = (layer: LayerItem) => {
        addToHistory();
        setLayers([...layers, { ...layer, id: uuidv4() }]);
    };

    const checkDeselect = (e: KonvaEventObject<MouseEvent>) => {
        const clickedOnEmpty = e.target === e.target.getStage();
        if (clickedOnEmpty) {
            setSelectedId(null);
            if (transformerRef.current) {
                transformerRef.current.nodes([]);
                const layer = transformerRef.current.getLayer();
                if (layer) {
                    layer.batchDraw();
                }
            }
        }
    };

    const onSubmit = (data: TemplateFormConfig) => {
        const fallbackStyle: TemplateStyle = {
            textColor: "#000000",
            backgroundColor: "#ffffff",
            cardBackgroundColor: "#ffffff",
        };
        onSave({
            ...template,
            name: data.name,
            scene: {
                elements: layers,
                style: {
                    ...fallbackStyle,
                    ...v1Scene.style,
                    accentColor,
                },
            },
        });
    };

    const onImagesDrop = async (files: File[]) => {
        if (!files.length) return;

        for (const file of files) {
            try {
                // Upload image using template service
                const imageUrl = await templateService.uploadImage(file);

                // Preload the image
                const img = new window.Image();
                img.src = `${import.meta.env.VITE_API_URL}${imageUrl}`;

                img.onload = () => {
                    // Calculate aspect ratio
                    const aspectRatio = img.width / img.height;

                    // Determine dimensions based on aspect ratio
                    // Use a reasonable max width/height for the template
                    const maxWidth = BASE_STAGE_WIDTH * 0.5;
                    let width = maxWidth;
                    let height = width / aspectRatio;

                    // Create a new image layer
                    const layerNumber = getNextLayerNumber("image");
                    const newLayer = getDefaultImageItem(
                        layerNumber,
                        BASE_STAGE_WIDTH / 2,
                        1080 / 2,
                    );

                    // Set image properties
                    (newLayer as ImageItem).imageUrl = imageUrl;
                    (newLayer as ImageItem).width = width;
                    (newLayer as ImageItem).height = height;

                    // Add to images cache
                    setImages((prev) => ({
                        ...prev,
                        [newLayer.id]: img,
                    }));

                    // Add to layers
                    addToHistory();
                    setLayers((prevLayers) => [...prevLayers, newLayer]);
                    setSelectedId(newLayer.id);
                };
            } catch (error) {
                console.error("Error uploading image:", error);
                toast.error(t("templates.editor.uploadError"));
            }
        }
    };

    return (
        <div className={styles.templateEditorContainer}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.layerControls}>
                <FormField
                    label={t("templates.edit.name")}
                    className="w-full"
                    required
                    registration={register("name")}
                    error={errors.name?.message}
                    touched={touchedFields.name}
                />

                <AccentColorField
                    value={accentColor}
                    onChange={setAccentColor}
                    className="mb-2 justify-between"
                />

                <h2 className={styles.layerControlsTitle}>{t("templates.layers")}</h2>

                {layers.length === 0 && (
                    <div className="flex items-center gap-2 justify-center">
                        <span className="text-sm text-muted-foreground">
                            {t("templates.noLayers")}
                        </span>
                    </div>
                )}

                {layers.length > 0 && (
                    <div className="flex flex-col gap-2 mb-4">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={layers.map((layer) => layer.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {layers.map((layer) => (
                                    <SortableLayerPreview
                                        key={layer.id}
                                        layer={layer}
                                        isSelected={selectedId === layer.id}
                                        onSelect={setSelectedId}
                                        onNameChange={(id, newName) => {
                                            addToHistory();
                                            setLayers(
                                                layers.map((layer) =>
                                                    layer.id === id
                                                        ? { ...layer, name: newName }
                                                        : layer,
                                                ),
                                            );
                                        }}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                )}

                <div className={styles.divider} />

                <div className="flex items-center gap-2 flex-col w-full">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="w-full">
                                <PlusIcon className="w-2 h-2 mr-2" />
                                {t("templates.newLayer")}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            {layerTypes
                                .filter((layerType) => layerType.id !== "image")
                                .map((layerType) => (
                                    <DropdownMenuItem
                                        key={layerType.id}
                                        onClick={() => onAddLayer(layerType.id)}
                                    >
                                        {layerType.icon}
                                        {t(`templates.layerTypes.${layerType.id}`)}
                                    </DropdownMenuItem>
                                ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Button
                        className="w-full"
                        type="submit"
                        disabled={!isValid || layers.length === 0}
                    >
                        {t("general.save")}
                    </Button>
                </div>
            </form>

            <div className={styles.editorContainer} ref={containerRef}>
                <ActionBar
                    selected={selectedLayer}
                    onDelete={onDelete}
                    onChange={(element) => {
                        addToHistory();
                        setLayers(layers.map((l) => (l.id === element.id ? element : l)));
                    }}
                    onDuplicate={onDuplicate}
                />
                <div className={styles.canvasContainer}>
                    <FileDropzone
                        width={stageSize.width}
                        height={stageSize.height}
                        onImagesDrop={onImagesDrop}
                    />

                    <Stage
                        width={stageSize.width}
                        height={stageSize.height}
                        onClick={checkDeselect}
                        scaleX={stageSize.scale}
                        scaleY={stageSize.scale}
                        className={styles.stage}
                    >
                        <Layer>
                            {[...layers].reverse().map((layer) => {
                                if (layer.type === "text") {
                                    return (
                                        <TextElementEditor
                                            key={layer.id}
                                            textItem={layer as TextItem}
                                            onChange={(textItem) => {
                                                addToHistory();
                                                setLayers(
                                                    layers.map((l) =>
                                                        l.id === textItem.id ? textItem : l,
                                                    ),
                                                );
                                            }}
                                            setSelectedId={setSelectedId}
                                            selectedId={selectedId}
                                        />
                                    );
                                }
                                if (layer.type === "image" && images[layer.id]) {
                                    const image = images[layer.id];
                                    if (!image) return null;

                                    return (
                                        <ImageElementEditor
                                            key={layer.id}
                                            imageItem={layer as ImageItem}
                                            onChange={(imageItem) => {
                                                addToHistory();
                                                setLayers(
                                                    layers.map((l) =>
                                                        l.id === imageItem.id ? imageItem : l,
                                                    ),
                                                );
                                            }}
                                            setSelectedId={setSelectedId}
                                            selectedId={selectedId}
                                            addToHistory={addToHistory}
                                            setLayers={setLayers}
                                            layers={layers}
                                            image={image}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </Layer>
                    </Stage>
                </div>
            </div>
        </div>
    );
};
